/**
 * Site Settings V2 — "Location" section: geographic coordinates and
 * time zone, which drive sunrise/sunset scheduling.
 *
 * Contains:
 * - LocationSection (default export)
 */

import LocationOnIcon from '@mui/icons-material/LocationOn';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  SettingsFieldRow,
  SettingsSection,
  SettingsSubsection,
  SettingsTextField,
  SettingsTimeZoneField,
  UseDeviceLocationButton,
} from '@sophocles/views/settings/components';

export default function LocationSection({ canEdit }) {
  const { t } = useTranslation();
  return (
    <SettingsSection
      id="settings-location"
      icon={<LocationOnIcon />}
      title={t('siteSettings.v2.location', 'Location')}
      description={t(
        'siteSettings.v2.locationDescription',
        'Geographic coordinates and time zone — drive sunset/sunrise scheduling.',
      )}
    >
      <SettingsSubsection title={t('siteSettings.v2.coordinates', 'Coordinates & Time Zone')} bordered>
        <SettingsFieldRow
          label={t('general.timeZone', 'Time Zone')}
          description={t(
            'siteSettings.v2.timeZoneDescription',
            'Drives sunrise/sunset scheduling and timestamp display.',
          )}
          controlWidth={380}
          required
        >
          <SettingsTimeZoneField name="location.time_zone" hiddenLabel showCurrentTime={false} />
        </SettingsFieldRow>
        <SettingsFieldRow
          label={t('general.latitude', 'Latitude')}
          description={t('siteSettings.v2.latitudeDescription', 'Decimal degrees; positive north, negative south.')}
          controlWidth={200}
          required
        >
          <SettingsTextField name="location.latitude" hiddenLabel fullWidth />
        </SettingsFieldRow>
        <SettingsFieldRow
          label={t('general.longitude', 'Longitude')}
          description={t('siteSettings.v2.longitudeDescription', 'Decimal degrees; positive east, negative west.')}
          controlWidth={200}
          required
        >
          <SettingsTextField name="location.longitude" hiddenLabel fullWidth />
        </SettingsFieldRow>
        <SettingsFieldRow
          label={t('siteSettings.v2.useDeviceLocation', 'Use device location')}
          description={t(
            'siteSettings.v2.useDeviceLocationDescription',
            'Read current GPS coordinates from this browser/device into the latitude and longitude fields above.',
          )}
          controlWidth={240}
        >
          <UseDeviceLocationButton disabled={!canEdit} />
        </SettingsFieldRow>
      </SettingsSubsection>
    </SettingsSection>
  );
}
